import { type FC, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import './_dropdownSelect.scss'

const DropDownSelect: FC<{
    options: Array<{ color?: string; label: string; value: string }>
    placeholder: string
    search: string
    setSearch: (key: string) => void
    selectedOption: string
    setSelectedOption: (key: string) => void
    icon?: string | JSX.Element | JSX.Element[]
    iconWidth?: string | number
    customClass?: string
    notSearchable?: boolean
}> = ({
    options,
    placeholder,
    search,
    setSearch,
    selectedOption,
    setSelectedOption,
    icon,
    iconWidth,
    customClass,
    notSearchable = false
}) => {
    const inputElement = useRef<any>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const { t } = useTranslation()

    // Close dropdown on outside click
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setDropdownOpen(false)
            }
        }
        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClick)
        }
        return () => document.removeEventListener('mousedown', handleClick)
    }, [dropdownOpen])

    // Toggle handler for input and selected value
    const handleToggle = () => {
        setDropdownOpen(open => !open)
        if (!dropdownOpen) {
            inputElement.current?.focus()
        }
    }

    return (
        <div
            className={`dropdown-select-container ${customClass ?? ''}`}
            ref={containerRef}
        >
            {icon && (
                <div
                    className='dropdown-select-icon'
                    style={{
                        width: `${iconWidth ? `${iconWidth}px` : '50px'}`
                    }}
                    onClick={() => inputElement.current.focus()}
                >
                    {typeof icon === 'string' ? (
                        <img src={`${icon}`} alt={t('dropdownIcon')} />
                    ) : (
                        icon
                    )}
                </div>
            )}

            <input
                ref={inputElement}
                readOnly={notSearchable}
                type='text'
                placeholder={placeholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={`dropdown-select-input ${dropdownOpen && 'dropdown-select-input-open'}`}
                style={{
                    paddingLeft: icon
                        ? `${iconWidth ? `${iconWidth}px` : '50px'}`
                        : '15px'
                }}
                // CHANGED: Use click to toggle
                onClick={handleToggle}
            />

            <div
                className={`dropdown-select-options ${
                    dropdownOpen && 'dropdown-select-options-open'
                }`}
            >
                {search.trim() !== ''
                    ? options
                          ?.filter(option =>
                              option.label
                                  .toLowerCase()
                                  .includes(search.toLowerCase())
                          )
                          .map(option => (
                              <div
                                  key={option.value}
                                  className='dropdown-select-option'
                                  onClick={() => {
                                      setSelectedOption(option.value)
                                      setDropdownOpen(false)
                                      setSearch('')
                                  }}
                              >
                                  {option.label}
                              </div>
                          ))
                    : options?.map(option => (
                          <div
                              key={option.value}
                              className='dropdown-select-option'
                              onClick={() => {
                                  setSelectedOption(option.value)
                                  setDropdownOpen(false)
                                  setSearch('')
                              }}
                          >
                              {option.color && (
                                  <div
                                      className='colorDots'
                                      style={{ backgroundColor: option.color }}
                                  ></div>
                              )}
                              {option.label}
                          </div>
                      ))}
            </div>
            <div
                className={`dropdown-select-selected-option ${
                    !dropdownOpen &&
                    selectedOption !== '' &&
                    'dropdown-select-selected-option-open'
                }`}
                // CHANGED: Toggle, not just open
                onClick={handleToggle}
                style={{
                    left: icon
                        ? `${iconWidth ? `${iconWidth}px` : '50px'}`
                        : '15px'
                }}
            >
                {
                    options?.filter(option =>
                        option.value.includes(selectedOption)
                    )?.[0]?.label
                }
            </div>
        </div>
    )
}

export default DropDownSelect
